import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { SL_Input } from "../../../components/input/Input";
import { Alert, Form, Modal, Tag, message } from "antd";
import { SL_Select } from "../../../components/selects/Select";

// Opções alinhadas ao CreateGymWorkoutModal para manter consistência entre os modais
const GENDER_OPTIONS = [
	{ value: "male", label: displayMessage("MALE") },
	{ value: "female", label: displayMessage("FEMALE") },
];

const FREQUENCY_OPTIONS = [
	{ value: 1, label: "1x por semana" },
	{ value: 2, label: "2x por semana" },
	{ value: 3, label: "3x por semana" },
	{ value: 4, label: "4x por semana" },
	{ value: 5, label: "5x por semana" },
	{ value: 6, label: "6x por semana" },
	{ value: 7, label: "7x por semana" },
];

const BODY_PAIN_OPTIONS = [
	{ value: 'none', label: 'Não sinto dores' },
	{ value: 'lower_back', label: 'Na lombar' },
	{ value: 'shoulder', label: 'No ombro' },
	{ value: 'knee', label: 'No joelho' },
];

const DURATION_OPTIONS = [
	{ value: 40, label: '40 min' },
	{ value: 60, label: '60 min' },
	{ value: 90, label: '90 min' },
];

// Opções de músculo foco (target_muscle). NULL/vazio = "Nenhum (genérico)" —
// aparece para qualquer foco escolhido pelo usuário (fallback).
const TARGET_MUSCLE_OPTIONS = [
	{ value: 'none', label: 'Nenhum (genérico)' },
	{ value: 'legs', label: 'Pernas' },
	{ value: 'glutes', label: 'Glúteos' },
	{ value: 'chest', label: 'Peito' },
	{ value: 'back', label: 'Costas' },
	{ value: 'shoulders', label: 'Ombros' },
	{ value: 'arms', label: 'Braços' },
];

// Chave da combinação + sequência, usada para detectar se o destino
// já possui treinos (split ativo que os usuários do app podem estar usando).
const buildComboSeqKey = (w) =>
	`${w.gender}|${w.weekly_frequency}|${w.body_pain}|${w.duration_minutes}|${w.target_muscle ?? ''}|${w.sequence ?? 1}`;

/**
 * Modal de duplicação de treinos.
 *
 * - `workoutsToDuplicate`: treinos selecionados no grid (todos da mesma combinação).
 * - `allWorkouts`: lista completa de treinos, usada apenas para avisar quando o
 *   destino escolhido já possui treinos (as cópias entrariam num split ativo).
 * - Os campos de destino vêm pré-preenchidos com a combinação/sequência de origem.
 */
export function DuplicateGymWorkoutsModal({ onCloseModal, workoutsToDuplicate, allWorkouts }) {
	const [form] = Form.useForm();
	const { setShowLoading } = useContext(GeneralContext);

	// Valores atuais do formulário, para recalcular o aviso de destino ocupado
	const [targetValues, setTargetValues] = useState(null);

	const sourceWorkouts = workoutsToDuplicate || [];
	const firstSource = sourceWorkouts[0];

	// Pré-preenche o destino com a combinação/sequência do(s) treino(s) de origem
	useEffect(() => {
		if (firstSource) {
			const initial = {
				gender: firstSource.gender,
				weekly_frequency: firstSource.weekly_frequency,
				body_pain: firstSource.body_pain || 'none',
				duration_minutes: firstSource.duration_minutes,
				sequence: firstSource.sequence ?? 1,
				target_muscle: firstSource.target_muscle || 'none',
			};
			form.setFieldsValue(initial);
			setTargetValues(initial);
		}
	}, [firstSource, form]);

	// O destino já possui treinos cadastrados? Se sim, as cópias entram nesse
	// split — e usuários ativos nessa combinação/sequência veem a mudança na hora.
	const targetHasWorkouts = (() => {
		if (!targetValues) return false;
		const targetKey = buildComboSeqKey({
			...targetValues,
			sequence: targetValues.sequence != null ? parseInt(targetValues.sequence, 10) : 1,
		});
		return (allWorkouts || []).some((w) => buildComboSeqKey(w) === targetKey);
	})();

	async function onFinishForm(values) {
		try {
			setShowLoading(true);

			await api.gymWorkouts.duplicate({
				workout_ids: sourceWorkouts.map((w) => w.id),
				target: {
					gender: values.gender,
					weekly_frequency: values.weekly_frequency,
				body_pain: values.body_pain,
				duration_minutes: values.duration_minutes,
				sequence: values.sequence,
				target_muscle: values.target_muscle,
			},
		});

			message.success(
				sourceWorkouts.length > 1
					? `${sourceWorkouts.length} treinos duplicados com sucesso!`
					: 'Treino duplicado com sucesso!'
			);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao duplicar treinos:", error);
		} finally {
			setShowLoading(false);
		}
	}

	return (
		<Modal
			title={
				<div className="text-xl font-bold text-blue-700 mr-3">
					Duplicar {sourceWorkouts.length > 1 ? `${sourceWorkouts.length} treinos` : 'treino'}
				</div>
			}
			open={true}
			onCancel={() => onCloseModal(false)}
			centered={true}
			footer={
				<div className="flex flex-col ss:flex-row gap-2 justify-end">
					<SL_Button
						className="w-full ss:w-[150px] bg-gray-500 border-transparent"
						onClick={() => onCloseModal(false)}
					>
						{displayMessage("CLOSE")}
					</SL_Button>

					<SL_Button
						className="w-full ss:w-[150px] m-0 bg-blue-600"
						onClick={() => form.submit()}
					>
						Duplicar
					</SL_Button>
				</div>
			}
			maskClosable={false}
			width={600}
		>
			<div className="mb-3">
				<div className="text-blue-700 font-semibold mb-1">
					Treinos que serão duplicados (com todos os exercícios):
				</div>
				<div className="flex flex-wrap gap-1">
					{sourceWorkouts.map((w) => (
						<Tag key={w.id} color="blue">
							{w.name}
						</Tag>
					))}
				</div>
			</div>

			<Form
				onFinish={onFinishForm}
				layout="vertical"
				form={form}
				onValuesChange={(_, allValues) => setTargetValues(allValues)}
				className={`flex gap-2 flex-col`}
			>
				<div className="text-blue-700 font-semibold">
					Combinação de destino das cópias:
				</div>

				<div className="gap-4 flex w-full flex-col smb:flex-row">
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("GENDER")}:
							</span>
						}
						name="gender"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("GENDER_MANDATORY") },
						]}
					>
						<SL_Select options={GENDER_OPTIONS} />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("WEEKLY_FREQUENCY")}:
							</span>
						}
						name="weekly_frequency"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("FREQUENCY_MANDATORY") },
						]}
					>
						<SL_Select options={FREQUENCY_OPTIONS} />
					</Form.Item>
				</div>

				<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								Dor no corpo:
							</span>
						}
						name="body_pain"
						className={`w-full m-0`}
					>
						<SL_Select options={BODY_PAIN_OPTIONS} />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("DURATION_MINUTES")}:
							</span>
						}
						name="duration_minutes"
						className={`w-full m-0`}
					>
						<SL_Select options={DURATION_OPTIONS} />
					</Form.Item>
				</div>

				<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("SEQUENCE")}:
							</span>
						}
						name="sequence"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("SEQUENCE_MANDATORY") },
						]}
						tooltip={displayMessage("SEQUENCE_HELPER")}
					>
						<SL_Input type="number" className="text-blue-700" />
					</Form.Item>
				</div>

				<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								Músculo foco:
							</span>
						}
						name="target_muscle"
						className={`w-full m-0`}
						tooltip='Deixe em "Nenhum (genérico)" para que as cópias apareçam para qualquer foco. Defina um músculo para criar variações de foco a partir dos treinos selecionados.'
					>
						<SL_Select options={TARGET_MUSCLE_OPTIONS} />
					</Form.Item>
				</div>

				{targetHasWorkouts && (
					<Alert
						className="mt-2"
						type="warning"
						showIcon
						message="Atenção: destino já possui treinos"
						description="A combinação/sequência escolhida já tem treinos cadastrados. As cópias entrarão nesse conjunto e ficarão visíveis imediatamente para os usuários do app que estão nessa combinação e sequência."
					/>
				)}
			</Form>
		</Modal>
	);
}
